<template>
  <v-container v-if="data_transactions" fluid class="pa-6 bg-light-soft min-h-screen">
    <v-row>
      <v-col cols="12">
        <!-- Header Section -->
        <v-card class="rounded-xl border-soft shadow-sm bg-white mb-8 px-6 py-5" elevation="0">
          <div class="d-flex align-center justify-space-between" style="gap: 16px">
            <div class="d-flex align-center" style="gap: 16px">
              <v-btn v-if="hasHistory" icon @click="$router.go(-1)" class="bg-light-soft">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-avatar color="primary lighten-5" size="56" class="rounded-xl">
                <v-icon color="primary" size="30">mdi-wallet-outline</v-icon>
              </v-avatar>
              <div>
                <h1 class="text-h4 font-weight-black grey--text text--darken-4 mb-2">
                  Pago #{{ filLeft(data_transactions[0].invoice_payment_id.toString(), 8, "0") }}
                </h1>
                <div class="d-flex align-center" style="gap: 8px">
                  <v-chip :color="data_transactions[0].recorded ? 'success' : 'warning'" small
                    class="font-weight-bold" outlined>
                    <v-icon small left>{{ data_transactions[0].recorded ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                    {{ data_transactions[0].recorded ? 'Contabilizado' : 'Pendiente Contabilidad' }}
                  </v-chip>
                  <v-chip color="grey lighten-3" small class="grey--text text--darken-2 font-weight-medium">
                    <v-icon small left>mdi-office-building-outline</v-icon>
                    {{ data_transactions[0].business_unit_description || 'Unidad de Negocio' }}
                  </v-chip>
                </div>
              </div>
            </div>

            <div class="d-flex flex-wrap align-center" style="gap: 10px">
              <v-btn
                v-if="hasFeature('CXP-042') && data_transactions[0].recorded === false && (data_transactions[0].status_id === 3 || data_transactions[0].status_id === 1)"
                color="primary" depressed class="rounded-lg text-capitalize font-weight-bold"
                :loading="loading" @click="accountingPayment">
                <v-icon left size="18">mdi-book-check-outline</v-icon>
                Contabilizar
              </v-btn>

              <v-btn
                v-if="hasFeature('CXP-042') && data_transactions[0].recorded === false && data_transactions[0].status_id != 4"
                color="error" outlined depressed class="rounded-lg text-capitalize font-weight-bold"
                :loading="loading" @click="cancelPaymentDialog = true">
                <v-icon left size="18">mdi-cancel</v-icon>
                Anular pago
              </v-btn>

              <v-btn v-if="hasFeature('CXP-042') && data_transactions[0].recorded && isInAccountingPeriod" color="warning" depressed
                class="rounded-lg text-capitalize font-weight-bold"
                :loading="loading" @click="cancelAccountingPayment">
                <v-icon left size="18">mdi-book-remove-outline</v-icon>
                Descontabilizar
              </v-btn>

              <v-btn
                v-if="hasFeature('CXP-042') && data_transactions[0].recorded === false && data_transactions[0].status_id != 4"
                color="purple" outlined depressed class="rounded-lg text-capitalize font-weight-bold"
                :loading="loading" @click="openEditDates">
                <v-icon left size="18">mdi-calendar-edit</v-icon>
                Editar Fechas
              </v-btn>

              <v-divider vertical class="mx-1" style="height: 32px; align-self: center"></v-divider>

              <v-btn color="grey lighten-3" depressed class="rounded-lg grey--text text--darken-2 font-weight-medium"
                @click="generarReportePago()">
                <v-icon left size="18">mdi-printer</v-icon>
                Imprimir
              </v-btn>

              <v-menu offset-y left v-if="data_transactions[0].status_id == 2 && hasFeature('CXP-042')">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" class="bg-light-soft">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense class="rounded-lg py-1">
                  <v-list-item @click="movePayment">
                    <v-list-item-icon class="mr-3"><v-icon small color="success">mdi-check-circle</v-icon></v-list-item-icon>
                    <v-list-item-title class="success--text font-weight-bold">Mover a pagado</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </v-card>

        <!-- Summary Cards -->
        <v-row class="mb-6">
          <v-col cols="12" sm="6" md="3" class="d-flex">
            <v-card class="rounded-xl border-soft shadow-sm pa-5 bg-white transition-all flex-grow-1" elevation="0">
              <div class="d-flex align-center mb-4">
                <v-avatar color="blue lighten-5" size="36" class="mr-3">
                  <v-icon color="blue" small>mdi-bank</v-icon>
                </v-avatar>
                <div>
                  <span class="text-caption font-weight-black grey--text text-uppercase letter-spacing-1">Cuenta
                    Origen</span>
                  <div class="text-body-1 font-weight-black grey--text text--darken-4 text-truncate"
                    style="max-width: 180px">
                    {{ data_transactions[0].bank_account_description_long }}
                  </div>
                </div>
              </div>
              <v-divider class="mb-4"></v-divider>
              <div class="d-flex justify-space-between align-center"
                :class="{ 'mb-1': data_transactions[0].payment_details && data_transactions[0].payment_details[0]?.bank_comision > 0 }">
                <span class="text-caption grey--text font-weight-medium">Número</span>
                <span class="text-body-2 font-weight-bold grey--text text--darken-3">{{
                  data_transactions[0].bank_account_description_short }}</span>
              </div>
              <div v-if="data_transactions[0].check_number"
                class="d-flex justify-space-between align-center mt-1">
                <span class="text-caption grey--text font-weight-medium">No. Cheque</span>
                <span class="text-body-2 font-weight-bold grey--text text--darken-3">{{
                  data_transactions[0].check_number }}</span>
              </div>
              <div v-if="data_transactions[0].payment_details && data_transactions[0].payment_details[0]?.bank_comision > 0"
                class="d-flex justify-space-between align-center">
                <span class="text-caption grey--text font-weight-medium">Comisión Bancaria</span>
                <span class="text-body-2 font-weight-bold warning--text">
                  {{ currencyFormat(data_transactions[0].payment_details[0].bank_comision * data_transactions[0].payment_details.length) }}
                  <span class="text-caption grey--text">{{ data_transactions[0].currency_code }}</span>
                </span>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3" class="d-flex">
            <v-card class="rounded-xl border-soft shadow-sm pa-5 bg-white transition-all flex-grow-1" elevation="0">
              <div class="d-flex align-center mb-4">
                <v-avatar color="orange lighten-5" size="36" class="mr-3">
                  <v-icon color="orange" small>mdi-cash-multiple</v-icon>
                </v-avatar>
                <div>
                  <span class="text-caption font-weight-black grey--text text-uppercase letter-spacing-1">Método de
                    Pago</span>
                  <div class="text-body-1 font-weight-black grey--text text--darken-4">{{
                    data_transactions[0].payment_type_name
                    }}</div>
                </div>
              </div>
              <v-divider class="mb-4"></v-divider>
              <div class="py-1">
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="text-caption grey--text font-weight-medium">Monto Transacción</span>
                  <div class="text-h6 font-weight-black primary--text">
                    {{ currencyFormat(data_transactions[0].total_amount) }}
                    <span class="text-caption grey--text">{{ data_transactions[0].currency_code }}</span>
                  </div>
                </div>
                <div
                  v-if="data_transactions[0].payment_details && data_transactions[0].payment_details.length > 0 && data_transactions[0].payment_details[0].business_currency_id !== data_transactions[0].currency_id"
                  class="d-flex justify-space-between align-center">
                  <span class="text-caption grey--text font-weight-medium">Moneda Base</span>
                  <div class="text-body-2 font-weight-bold grey--text text--darken-2">
                    {{ currencyFormat(data_transactions[0].total_amount * data_transactions[0].payment_details[0].business_unit_currency_rate) }}
                    <span class="text-caption grey--text">{{ data_transactions[0].payment_details[0].business_currency_code }}</span>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3" class="d-flex">
            <v-card class="rounded-xl border-soft shadow-sm pa-5 bg-white transition-all flex-grow-1" elevation="0">
              <div class="d-flex align-center mb-4">
                <v-avatar color="purple lighten-5" size="36" class="mr-3">
                  <v-icon color="purple" small>mdi-calendar-clock</v-icon>
                </v-avatar>
                <div>
                  <span class="text-caption font-weight-black grey--text text-uppercase letter-spacing-1">Fechas</span>
                  <div class="text-body-1 font-weight-black grey--text text--darken-4">Cronología</div>
                </div>
              </div>
              <v-divider class="mb-4"></v-divider>
              <div class="d-flex justify-space-between align-center mb-1">
                <span class="text-caption grey--text font-weight-medium">Efectiva</span>
                <span class="text-body-2 font-weight-bold grey--text text--darken-3">{{
                  getDate(data_transactions[0].payment_date) }}</span>
              </div>
              <div class="d-flex justify-space-between align-center mb-1">
                <span class="text-caption grey--text font-weight-medium">Contable</span>
                <span class="text-body-2 font-weight-bold grey--text text--darken-3">{{
                  getDate(data_transactions[0].account_date) }}</span>
              </div>
              <div class="d-flex justify-space-between align-center">
                <span class="text-caption grey--text font-weight-medium">Creación</span>
                <span class="text-body-2 font-weight-bold grey--text text--darken-3">{{
                  getDate(data_transactions[0].created_at) }}</span>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3" class="d-flex">
            <v-card class="rounded-xl border-soft shadow-sm pa-5 bg-white transition-all flex-grow-1" elevation="0">
              <div class="d-flex align-center mb-4">
                <v-avatar color="success lighten-5" size="36" class="mr-3">
                  <v-icon color="success" small>mdi-account-check</v-icon>
                </v-avatar>
                <div>
                  <span
                    class="text-caption font-weight-black grey--text text-uppercase letter-spacing-1">Responsable</span>
                  <div class="text-body-1 font-weight-black grey--text text--darken-4 text-truncate"
                    style="max-width: 150px">
                    {{ data_transactions[0].payment_by_name }}
                  </div>
                </div>
              </div>
              <v-divider class="mb-4"></v-divider>
              <div class="d-flex justify-space-between align-center">
                <span class="text-caption grey--text font-weight-medium">Estado</span>
                <v-chip color="success" x-small class="font-weight-bold px-2">{{ data_transactions[0].status_name
                  }}</v-chip>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tabs for Invoices and Accounting -->
        <v-tabs v-model="tab" background-color="transparent" color="primary" class="mb-6 custom-tabs">
          <v-tab class="text-capitalize font-weight-bold">
            <v-icon left>mdi-receipt-text-outline</v-icon>
            Facturas Pagadas
          </v-tab>
          <v-tab v-if="data_transactions[0].recorded" class="text-capitalize font-weight-bold">
            <v-icon left>mdi-finance</v-icon>
            Entrada de Diario
          </v-tab>
          <v-tab class="text-capitalize font-weight-bold">
            <v-icon left>mdi-file-document-outline</v-icon>
            Archivos Adjuntos
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab" class="bg-transparent">
          <!-- Paid Invoices Item -->
          <v-tab-item>
            <div v-for="(entityGroup, index) in data_transactions_entity" :key="index" class="mb-6">
              <v-card class="rounded-xl border-soft shadow-sm bg-white overflow-hidden" elevation="0">
                <v-toolbar flat color="primary lighten-5" class="px-4">
                  <v-avatar color="primary" size="32" class="mr-3">
                    <span class="text-caption font-weight-black white--text">{{ getInitials(entityGroup[0].entity_name)
                      }}</span>
                  </v-avatar>
                  <div>
                    <div class="text-subtitle-1 font-weight-black grey--text text--darken-4">{{
                      entityGroup[0].entity_name }}
                    </div>
                    <div class="d-flex align-center text-tiny grey--text font-weight-medium mt-n1" style="gap: 5px">
                      <span class="font-weight-bold uppercase letter-spacing-1">{{ entityGroup[0].entity_document_id }}</span>
                      <template v-if="entityGroup[0].supplier_account">
                        <span class="grey--text text--lighten-1">·</span>
                        <v-icon x-small color="blue">mdi-bank</v-icon>
                        <span class="font-weight-bold">{{ entityGroup[0].bank_description_long || 'Banco' }}</span>
                        <span>· Cta. {{ entityGroup[0].supplier_account }}</span>
                        <span>· {{ entityGroup[0].account_name || entityGroup[0].entity_name }}</span>
                      </template>
                    </div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-chip color="primary" x-small class="font-weight-bold" outlined>
                    {{ entityGroup.length }} FACTURA(S)
                  </v-chip>
                </v-toolbar>
                <v-divider></v-divider>

                <v-data-table :headers="computedHeaders" :items="entityGroup" :items-per-page="-1" dense class="custom-table" hide-default-footer>
                  <template v-slot:[`item.invoice_number`]="{ item }">
                    <div class="d-flex align-center py-2">
                      <v-icon color="primary" small class="mr-2">mdi-file-document-outline</v-icon>
                      <div>
                        <div class="text-body-2 font-weight-bold primary--text cursor-pointer"
                          @click="showInvoice(item.invoice_id, true)">
                          #{{ item.invoice_number }}
                        </div>
                        <div class="text-tiny grey--text font-weight-medium">Ref: {{ item.supplier_invoice_number ||
                          '---' }}</div>
                      </div>
                    </div>
                  </template>

                  <template v-slot:[`item.total_amount`]="{ item }">
                    <div class="text-right">
                      <span class="text-body-2 grey--text font-weight-medium">{{ currencyFormat(item.total_amount)
                        }}</span>
                      <span class="text-tiny grey--text ml-1">{{ item.currency_code }}</span>
                    </div>
                  </template>

                  <template v-slot:[`item.total_pending_amount`]="{ item }">
                    <div class="text-right">
                      <span class="text-body-2 error--text font-weight-bold">
                        {{ currencyFormat(parseFloat(item.total_amount) - parseFloat(item.total_paid_amount)) }}
                      </span>
                    </div>
                  </template>

                  <template v-slot:[`item.total_paid_amount`]="{ item }">
                    <div class="text-right">
                      <v-chip color="success lighten-5" class="success--text font-weight-black" x-small label>
                        {{ currencyFormat(item.payment_amount) }} {{ item.currency_code }}
                      </v-chip>
                    </div>
                  </template>

                  <template v-slot:[`item.sta_description`]="{ item }">
                    <div class="text-right">
                      <v-chip x-small class="font-weight-bold" color="grey lighten-4">{{ item.sta_description
                        }}</v-chip>
                    </div>
                  </template>

                  <template v-slot:[`item.actions`]="{ item }">
                    <div class="text-right">
                      <v-btn icon x-small color="error" @click="removeInvoiceConfirm(item)"
                        v-if="hasFeature('CXP-042') && data_transactions[0].payment_details.length > 1 && isInAccountingPeriod" :loading="loading">
                        <v-icon small>mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>

                <v-divider></v-divider>
                <div class="py-2 px-4 bg-light-soft text-right">
                  <span class="text-caption grey--text font-weight-bold uppercase letter-spacing-1 mr-2">Total
                    Beneficiario:</span>
                  <span class="text-subtitle-1 font-weight-black primary--text">{{ currencyFormat(getTotalPaid(entityGroup)) }}
                    {{
                      entityGroup[0].payment_currency_code }}</span>
                </div>
              </v-card>
            </div>
          </v-tab-item>

          <!-- Accounting Entry Item -->
          <v-tab-item v-if="data_transactions[0].recorded">
            <v-card class="rounded-xl border-soft shadow-sm bg-white overflow-hidden" elevation="0">
              <v-toolbar flat color="white" class="px-4 pt-2">
                <v-icon color="primary" class="mr-2">mdi-notebook-outline</v-icon>
                <span class="text-h6 font-weight-black grey--text text--darken-4">Detalle de Asientos Contables</span>
                <v-spacer></v-spacer>
                <span class="text-caption grey--text font-weight-bold uppercase letter-spacing-1">Asiento: {{
                  accounting[0] ?
                    accounting[0].diary_book_code : '---' }}</span>
              </v-toolbar>
              <v-divider></v-divider>

              <v-data-table :headers="headersContabilidad" :items-per-page="-1" :items="accounting" dense class="custom-table"
                hide-default-footer>
                <template v-slot:[`item.chart_of_account_name`]="{ item }">
                  <div class="py-2">
                    <div class="text-body-2 font-weight-bold grey--text text--darken-4">{{ item.chart_of_account_code }}
                    </div>
                    <div class="text-tiny grey--text font-weight-medium">{{ item.chart_of_account_name }}</div>
                  </div>
                </template>

                <template v-slot:[`item.debito`]="{ item }">
                  <div v-if="item.entry_type_id == 1" class="text-right success--text font-weight-bold">
                    {{ currencyFormat(item.amount) }}
                  </div>
                  <div v-else class="text-right grey--text lighten-3">---</div>
                </template>

                <template v-slot:[`item.credito`]="{ item }">
                  <div v-if="item.entry_type_id == 2" class="text-right error--text font-weight-bold">
                    {{ currencyFormat(item.amount) }}
                  </div>
                  <div v-else class="text-right grey--text lighten-3">---</div>
                </template>

                <template v-slot:[`item.debitoBase`]="{ item }">
                  <div v-if="item.entry_type_id == 1" class="text-right font-weight-medium">
                    {{ currencyFormat(item.amount * item.currency_rate) }}
                    <span class="text-tiny grey--text">{{ item.business_unit_currency_code }}</span>
                  </div>
                </template>

                <template v-slot:[`item.creditoBase`]="{ item }">
                  <div v-if="item.entry_type_id == 2" class="text-right font-weight-medium">
                    {{ currencyFormat(item.amount * item.currency_rate) }}
                    <span class="text-tiny grey--text">{{ item.business_unit_currency_code }}</span>
                  </div>
                </template>

                <template v-slot:body.append>
                  <tr class="bg-light-soft font-weight-black">
                    <td colspan="5" class="text-right uppercase letter-spacing-1 text-caption">Totales</td>
                    <td class="text-right success--text">{{ currencyFormat(getTotalDebito()) }}</td>
                    <td class="text-right error--text">{{ currencyFormat(getTotalCredito()) }}</td>
                    <td class="text-right success--text">{{ currencyFormat(getTotalDebitoBase()) }}</td>
                    <td class="text-right error--text">{{ currencyFormat(getTotalCreditoBase()) }}</td>
                  </tr>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

          <!-- Attachments Item -->
          <v-tab-item>
            <v-card class="rounded-xl border-soft shadow-sm bg-white pa-6 min-h-card" elevation="0">
              <div v-if="selectedAttached == ''"
                class="text-center py-12 border-dashed rounded-xl cursor-pointer hover-bg-light" @click="hasFeature('CXP-042') ? takeImage() : null">
                <v-avatar color="primary lighten-5" size="80" class="mb-4">
                  <v-icon color="primary" size="40">mdi-cloud-upload-outline</v-icon>
                </v-avatar>
                <div class="text-h6 font-weight-bold grey--text text--darken-3">No hay archivos adjuntos</div>
                <p class="text-body-2 grey--text">Haz clic aquí para subir un comprobante o documento relacionado.</p>
                <v-btn v-if="hasFeature('CXP-042')" color="primary" depressed class="rounded-lg text-capitalize px-6">Subir Archivo</v-btn>
              </div>
              <div v-else>
                <div class="d-flex align-center justify-space-between mb-4">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-file-check-outline</v-icon>
                    <span class="text-subtitle-1 font-weight-bold">Comprobante Adjunto</span>
                  </div>
                  <div class="d-flex gap-2">
                    <v-btn v-if="hasFeature('CXP-042')" color="primary" outlined small class="rounded-lg text-capitalize"
                      @click="takeImage">Cambiar</v-btn>
                    <v-btn v-if="hasFeature('CXP-042')" color="error" outlined small class="rounded-lg text-capitalize"
                      @click="selectedAttached = ''">Eliminar</v-btn>
                  </div>
                </div>
                <div class="rounded-xl overflow-hidden border shadow-sm">
                  <iframe :src="selectedAttached" frameborder="0" class="attached-iframe"></iframe>
                </div>
              </div>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>

    <!-- Hidden File Input -->
    <input type="file" id="fileInput" class="d-none" @change="onFileChange" accept="application/pdf,image/*">

    <!-- Confirm Cancel Payment Dialog -->
    <v-dialog v-model="cancelPaymentDialog" max-width="440" persistent>
      <v-card class="rounded-xl pa-2">
        <v-card-title class="text-subtitle-1 font-weight-black grey--text text--darken-4 pt-4 pb-2">
          <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
          Anular pago
        </v-card-title>
        <v-card-text class="grey--text text--darken-2 pb-2">
          ¿Estás seguro de que deseas anular el pago
          <span class="font-weight-bold primary--text">#{{ filLeft(data_transactions[0].invoice_payment_id.toString(), 8, '0') }}</span>?
          Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn text class="text-capitalize grey--text font-weight-bold" @click="cancelPaymentDialog = false">Cancelar</v-btn>
          <v-btn color="error" depressed class="rounded-lg text-capitalize font-weight-bold px-5"
            @click="cancelPayment" :loading="loading">
            <v-icon left small>mdi-cancel</v-icon>
            Anular
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Warning: must unpost before removing invoice -->
    <v-dialog v-model="removeWarningDialog" max-width="420">
      <v-card class="rounded-xl pa-2">
        <v-card-title class="text-subtitle-1 font-weight-black grey--text text--darken-4 pt-4 pb-2">
          <v-icon color="warning" class="mr-2">mdi-alert-outline</v-icon>
          Pago contabilizado
        </v-card-title>
        <v-card-text class="grey--text text--darken-2 pb-2">
          No es posible eliminar una factura de un pago contabilizado. Primero debes
          <span class="font-weight-bold warning--text">descontabilizar</span> el pago y luego podrás eliminar la factura.
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed class="rounded-lg text-capitalize font-weight-bold px-5"
            @click="removeWarningDialog = false">Entendido</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Remove Invoice Dialog -->
    <v-dialog v-model="confirmDialog" max-width="440" persistent>
      <v-card class="rounded-xl pa-2">
        <v-card-title class="text-subtitle-1 font-weight-black grey--text text--darken-4 pt-4 pb-2">
          <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
          Eliminar factura del pago
        </v-card-title>
        <v-card-text class="grey--text text--darken-2 pb-2">
          ¿Estás seguro de que deseas eliminar la factura
          <span class="font-weight-bold primary--text">#{{ invoiceToRemove ? invoiceToRemove.invoice_number : '' }}</span>
          de este pago? El monto del pago se ajustará automáticamente.
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn text class="text-capitalize grey--text font-weight-bold" @click="confirmDialog = false">Cancelar</v-btn>
          <v-btn color="error" depressed class="rounded-lg text-capitalize font-weight-bold px-5"
            @click="removeInvoice" :loading="loading">
            <v-icon left small>mdi-delete-outline</v-icon>
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dates Dialog -->
    <v-dialog v-model="editDatesDialog" max-width="440" persistent>
      <v-card class="rounded-xl pa-2">
        <v-card-title class="text-subtitle-1 font-weight-black grey--text text--darken-4 pt-4 pb-2">
          <v-icon color="purple" class="mr-2">mdi-calendar-edit</v-icon>
          Editar Fechas del Pago
        </v-card-title>
        <v-card-text class="pb-2">
          <v-row align="center" justify="center">
            <v-col cols="12">
              <v-menu v-model="menuPaymentDate" :close-on-content-click="false" transition="scale-transition"
                offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="editDatesFormatted.payment_date" label="Fecha Efectiva"
                    prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" dense outlined
                    hide-details="auto" class="mt-2"></v-text-field>
                </template>
                <v-date-picker v-model="editDates.payment_date"
                  @input="menuPaymentDate = false"></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row align="center" justify="center">
            <v-col cols="12">
              <v-menu v-model="menuAccountDate" :close-on-content-click="false" transition="scale-transition"
                offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="editDatesFormatted.account_date" label="Fecha Contable"
                    prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" dense outlined
                    :hint="datesAccountingEdit.start_date ? `Período abierto: ${datesAccountingEdit.start_date} al ${datesAccountingEdit.end_date}` : 'Sin período contable abierto'"
                    persistent-hint class="mt-2"></v-text-field>
                </template>
                <v-date-picker :min="datesAccountingEdit.start_date" :max="datesAccountingEdit.end_date"
                  v-model="editDates.account_date"
                  @input="menuAccountDate = false"></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn text class="text-capitalize grey--text font-weight-bold" @click="editDatesDialog = false">Cancelar</v-btn>
          <v-btn color="purple" dark depressed class="rounded-lg text-capitalize font-weight-bold px-5"
            @click="savePaymentDates" :loading="loading">
            <v-icon left small>mdi-content-save-outline</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="loading" z-index="100">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import global_reports from "@/services/global_reports.js";
import moment from "moment";

export default {
  data: () => ({
    tab: 0,
    headersContabilidad: [
      { text: "Asiento", align: "start", sortable: false, value: "diary_book_code" },
      { text: "Cuenta Contable", value: "chart_of_account_name" },
      { text: "Localidad", value: "location_description" },
      { text: "Entrada", value: "entity_name" },
      { text: "Cat/Sub", value: "category_name" },
      { text: "Déb Trans", value: "debito", align: "right" },
      { text: "Cré Trans", value: "credito", align: "right" },
      { text: "Déb Base", value: "debitoBase", align: "right" },
      { text: "Cré Base", value: "creditoBase", align: "right" },
    ],
    accounting: [],
    loading: false,
    id_transactions: null,
    data_transactions: null,
    data_transactions_entity: [],
    selectedAttached: "",
    headers: [
      { text: "Factura", align: "start", sortable: false, value: "invoice_number" },
      { text: "Monto Original", align: "end", value: "total_amount" },
      { text: "Saldo Pendiente", align: "end", value: "total_pending_amount" },
      { text: "Monto Pagado", value: "total_paid_amount", align: "end" },
      { text: "Estado", value: "sta_description", align: "end" },
    ],
    hasHistory: false,
    confirmDialog: false,
    invoiceToRemove: null,
    cancelPaymentDialog: false,
    removeWarningDialog: false,
    dates_accounting: { start_date: '', end_date: '' },
    editDatesDialog: false,
    editDates: { payment_date: '', account_date: '' },
    datesAccountingEdit: { start_date: '', end_date: '' },
    menuPaymentDate: false,
    menuAccountDate: false,
  }),
  computed: {
    computedHeaders() {
      return [
        ...this.headers,
        { text: "", value: "actions", align: "end", sortable: false },
      ];
    },
    editDatesFormatted() {
      return {
        payment_date: this.editDates.payment_date ? moment(this.editDates.payment_date).format("DD/MM/YYYY") : '',
        account_date: this.editDates.account_date ? moment(this.editDates.account_date).format("DD/MM/YYYY") : '',
      };
    },
    isInAccountingPeriod() {
      if (!this.dates_accounting.start_date || !this.data_transactions) return false;
      const accountDate = moment(this.data_transactions[0].account_date).format("YYYY-MM-DD");
      return accountDate >= this.dates_accounting.start_date && accountDate <= this.dates_accounting.end_date;
    },
  },
  created() {
    this.getData();
  },
  mounted() {
    this.hasHistory = window.history.length > 2;
  },
  methods: {
    takeImage() {
      document.getElementById("fileInput").click();
    },
    async onFileChange() {
      try {
        const fileInput = document.getElementById("fileInput");
        this.loading = true;
        let record = await this.upload_images("orders_atached", fileInput);

        const responseRequest = await this.api_Post(
          "transactions/add_storage_payment",
          {
            id: this.id_transactions,
            storage: record,
          }
        );
        if (responseRequest.status == 200) {
          this.$toast.success("Archivo adjunto guardado con éxito");
          this.getData();
        } else {
          this.$toast.error("Error al guardar el archivo");
        }
      } catch (e) {
        this.$toast.error("Error: " + e.message);
      } finally {
        this.loading = false;
      }
    },
    async cancelPayment() {
      this.cancelPaymentDialog = false;
      this.loading = true;
      let result = await this.api_Post("transactions/cancel_payment", {
        id_payments: this.id_transactions,
      });
      if (result.status == 200) {
        this.$toast.success("Pago anulado exitosamente");
        this.getData();
      } else {
        this.$toast.error("Error al anular el pago");
      }
      this.loading = false;
    },
    async getData() {
      this.loading = true;
      this.id_transactions = this.$route.params.id;

      const responseRequest = await this.api_Post("transactions/viewPayments", {
        id: this.id_transactions,
      });
      if (responseRequest.status == 200) {
        this.data_transactions = await responseRequest.json();

        const responseAccountig = await this.api_Post(
          "accounting/payment_accounting",
          {
            filters: `where PAYMENT_ID=${this.id_transactions}`,
          }
        );
        if (responseAccountig.status == 200) {
          this.accounting = await responseAccountig.json();
        }

        if (this.data_transactions[0].storage_json != null) {
          this.selectedAttached = await this.get_storage(
            this.data_transactions[0].storage_json
          );
        }
        this.data_transactions_entity = this.groupByEntityId(
          this.data_transactions[0]
        );

        const calendarRequest = await this.api_Post("accounting/get_calendar_accounting", {
          business_unit: this.data_transactions[0].business_unit_id,
          module: 4,
        });
        if (calendarRequest.status == 200) {
          const calendarData = await calendarRequest.json();
          if (calendarData.length > 0) {
            this.dates_accounting.start_date = moment(calendarData[0].from_date).format("YYYY-MM-DD");
            this.dates_accounting.end_date = moment(calendarData[0].to_date).format("YYYY-MM-DD");
          } else {
            this.dates_accounting = { start_date: '', end_date: '' };
          }
        }
      } else {
        this.$toast.error("Error al cargar los detalles del pago");
      }
      this.loading = false;
    },
    async accountingPayment() {
      this.loading = true;
      let result = await this.api_Post("transactions/accounting_payment", {
        payment_id: this.id_transactions,
      });
      if (result.status == 200) {
        await this.getData();
        this.$toast.success("Contabilización realizada con éxito");
      } else {
        this.$toast.error("Error al contabilizar el pago");
        this.loading = false;
      }
    },
    async cancelAccountingPayment() {
      this.loading = true;
      let result = await this.api_Post(
        "transactions/accounting_payment_cancel",
        {
          payment_id: this.id_transactions,
        }
      );
      if (result.status == 200) {
        this.$toast.success("Asiento contable revertido");
        await this.getData();
      } else {
        this.$toast.error("Error al revertir la contabilización");
        this.loading = false;
      }
    },
    groupByEntityId(array) {
      // Agrupa por suplidor + cuenta de banco (para que cada cuenta a la que se
      // pagó salga en su propia tabla). Si no hay cuenta (p. ej. pago en efectivo/
      // cheque sin cuenta de suplidor), agrupa solo por suplidor.
      const grouped = {};
      if (array.payment_details) {
        array.payment_details.forEach((item) => {
          const key = [
            item.entity_document_id,
            item.bank_id != null ? item.bank_id : "",
            item.supplier_account != null ? item.supplier_account : "",
          ].join("||");
          if (!grouped[key]) {
            grouped[key] = [];
          }
          item.payment_currency_code = array.currency_code;
          grouped[key].push(item);
        });
      }
      return Object.values(grouped);
    },
    getDate(date) {
      if (!date) return "---";
      return moment(date).format("DD/MM/YYYY");
    },
    filLeft(value, length, char) {
      return value.padStart(length, char);
    },
    getInitials(name) {
      if (!name) return "??";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    },
    getTotalDebito() {
      return this.accounting.reduce((acc, entry) => {
        return entry.entry_type_id === 1 ? acc + parseFloat(entry.amount) : acc;
      }, 0);
    },
    getTotalCredito() {
      return this.accounting.reduce((acc, entry) => {
        return entry.entry_type_id === 2 ? acc + parseFloat(entry.amount) : acc;
      }, 0);
    },
    getTotalDebitoBase() {
      return this.accounting.reduce((acc, entry) => {
        return entry.entry_type_id === 1 ? acc + parseFloat(entry.amount * entry.currency_rate) : acc;
      }, 0);
    },
    getTotalCreditoBase() {
      return this.accounting.reduce((acc, entry) => {
        return entry.entry_type_id === 2 ? acc + parseFloat(entry.amount * entry.currency_rate) : acc;
      }, 0);
    },
    getTotalPaid(entity) {
      return entity.reduce((acc, item) => acc + parseFloat(item.payment_amount), 0);
    },
    movePayment() {
      this.$toast.info("Función en desarrollo");
    },
    generarReportePago() {
      global_reports.generarReportePago(this.data_transactions[0]);
    },
    removeInvoiceConfirm(item) {
      if (this.data_transactions[0].recorded) {
        this.removeWarningDialog = true;
        return;
      }
      this.invoiceToRemove = item;
      this.confirmDialog = true;
    },
    async openEditDates() {
      this.editDates.payment_date = moment(this.data_transactions[0].payment_date).format("YYYY-MM-DD");
      this.editDates.account_date = moment(this.data_transactions[0].account_date).format("YYYY-MM-DD");
      const calendarRequest = await this.api_Post("accounting/get_calendar_accounting", {
        business_unit: this.data_transactions[0].business_unit_id,
        module: 3,
      });
      if (calendarRequest.status == 200) {
        const calendarData = await calendarRequest.json();
        if (calendarData.length > 0) {
          this.datesAccountingEdit.start_date = moment(calendarData[0].from_date).format("YYYY-MM-DD");
          this.datesAccountingEdit.end_date = moment(calendarData[0].to_date).format("YYYY-MM-DD");
        } else {
          this.datesAccountingEdit = { start_date: '', end_date: '' };
        }
      }
      this.editDatesDialog = true;
    },
    async savePaymentDates() {
      if (!this.datesAccountingEdit.start_date) {
        this.$toast.error("No hay período contable abierto para esta unidad de negocio");
        return;
      }
      if (this.editDates.account_date < this.datesAccountingEdit.start_date ||
          this.editDates.account_date > this.datesAccountingEdit.end_date) {
        this.$toast.error(`La fecha contable debe estar dentro del período abierto: ${this.datesAccountingEdit.start_date} al ${this.datesAccountingEdit.end_date}`);
        return;
      }
      this.loading = true;
      const result = await this.api_Post("transactions/update_payment_dates", {
        payment_id: this.id_transactions,
        payment_date: this.editDates.payment_date,
        account_date: this.editDates.account_date,
      });
      if (result.status == 200) {
        this.$toast.success("Fechas actualizadas exitosamente");
        this.editDatesDialog = false;
        await this.getData();
      } else {
        this.$toast.error("Error al actualizar las fechas");
        this.loading = false;
      }
    },
    async removeInvoice() {
      if (!this.invoiceToRemove) return;
      const payment = this.data_transactions[0];
      const remainingDetails = payment.payment_details.filter(
        (d) => d.invoice_id !== this.invoiceToRemove.invoice_id
      );
      const newTotalAmount = remainingDetails.reduce(
        (acc, d) => acc + parseFloat(d.payment_amount), 0
      );
      const inInvoicePayments = remainingDetails.map((d) => ({
        entity_id: d.entity_id,
        invoice_id: d.invoice_id,
        payment_amount: d.payment_amount,
        prepayment_id: null,
        prepayment_amount: null,
        excess_amount: null,
      }));
      const data = {
        in_payment_id: payment.invoice_payment_id,
        in_payment_type_id: payment.payment_type_id,
        in_invoice_type_id: payment.invoice_type_id,
        in_business_unit_id: payment.business_unit_id,
        in_location_id: payment.location_id,
        in_effective_date: payment.payment_date,
        in_account_date: payment.account_date,
        in_created_by: this.$store.state.auth.user.user.user_id,
        in_bank_account_id: payment.bank_account_id,
        in_currency_id: payment.currency_id,
        in_total_amount: newTotalAmount,
        in_storage_json: null,
        in_check_number: null,
        in_invoice_payments: inInvoicePayments,
        in_entity_payments: {
          excess_amount: null,
          excess_chart_of_account_id: null,
          note: null,
          currency_rate: 1,
          bank_comision: payment.payment_details[0]?.bank_comision > 0 ? payment.payment_details[0].bank_comision : null,
        },
      };
      this.confirmDialog = false;
      this.loading = true;
      const response = await this.api_Post("transactions/save_pay", data);
      if (response.status == 200) {
        this.$toast.success("Factura eliminada del pago exitosamente");
        await this.getData();
      } else {
        this.$toast.error("Error al eliminar la factura del pago");
        this.loading = false;
      }
      this.invoiceToRemove = null;
    },
  },
};
</script>

<style scoped>
.bg-light-soft {
  background-color: #f8fafc;
}

.border-soft {
  border: 1px solid #f1f5f9 !important;
}

.border-bottom {
  border-bottom: 1px solid #f1f5f9 !important;
}

.border-top {
  border-top: 1px solid #f1f5f9 !important;
}

.border-dashed {
  border: 2px dashed #e2e8f0 !important;
}

.shadow-sm {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03) !important;
}

.shadow-xs {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
}

.min-h-screen {
  min-height: 100vh;
}

.min-h-card {
  min-height: 400px;
}

.letter-spacing-1 {
  letter-spacing: 1px;
}

.uppercase {
  text-transform: uppercase;
}

.text-tiny {
  font-size: 0.7rem;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.transition-all {
  transition: all 0.3s ease;
}

.cursor-pointer {
  cursor: pointer;
}

.hover-bg-light:hover {
  background-color: #f1f5f9;
}

.custom-table {
  background-color: white !important;
}

.custom-table>>>th {
  background-color: #f8fafc !important;
  text-transform: uppercase;
  font-size: 0.65rem !important;
  font-weight: 800 !important;
  color: #64748b !important;
  letter-spacing: 0.05em;
  height: 36px !important;
}

.custom-table>>>td {
  height: 40px !important;
}

.attached-iframe {
  width: 100%;
  height: 600px;
  border: none;
}

.custom-tabs>>>.v-tabs-bar {
  height: 48px;
}

.custom-tabs>>>.v-tab {
  font-size: 0.875rem;
  letter-spacing: 0.025em;
}
</style>